import { config } from '@/lib/config';
import { withDemoSearch } from '@/lib/data-source';

/** Only recover code/stylesheet loading failures, never exceptions inside a page. */
export function isModuleLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|Unable to preload CSS/i.test(
    message,
  );
}

const RECOVERY_KEY = 'callsync:module-recovery';

type RecoveryEnvironment = {
  buildId: string;
  online: () => boolean;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  wait: (ms: number) => Promise<void>;
};

/** One retry, then one document refresh per build in this tab. Keep the full current URL. */
export async function loadRouteModule<T>(load: () => Promise<T>, env: RecoveryEnvironment): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (!isModuleLoadError(error) || !env.online()) throw error;
  }
  await env.wait(300);
  try {
    return await load();
  } catch (error) {
    if (!isModuleLoadError(error) || !env.online()) throw error;
    let recover = false;
    try {
      if (env.storage.getItem(RECOVERY_KEY) !== env.buildId) {
        // Persist before reloading; fail closed when storage is unavailable.
        env.storage.setItem(RECOVERY_KEY, env.buildId);
        recover = true;
      }
    } catch {
      /* Manual recovery still works in browsers that block session storage. */
    }
    if (recover) {
      env.reload();
      // Keep Suspense's loader visible until the replacement document arrives.
      // A blocked/cancelled refresh still falls back to manual recovery after a bound.
      await env.wait(8000);
    }
    throw error;
  }
}

export function recoverableImport<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    loadRouteModule(load, {
      buildId: __APP_BUILD_ID__,
      online: () => navigator.onLine,
      storage: {
        getItem: (key) => sessionStorage.getItem(key),
        setItem: (key, value) => sessionStorage.setItem(key, value),
      },
      reload: () => {
        // Demo mode is selected at document startup; preserve it even if navigation
        // failed before Root's URL-normalizing effect could run.
        if (config.isDemo && !new URLSearchParams(window.location.search).has('demo')) {
          window.location.replace(
            `${window.location.pathname}${withDemoSearch(window.location.search)}${window.location.hash}`,
          );
        } else window.location.reload();
      },
      wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    });
}
